import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { projectsApi, clientsApi } from '../api';
import { useCanEdit } from '../auth';
import { Plus, Search, FolderKanban, ChevronRight, Upload } from 'lucide-react';
import clsx from 'clsx';
import { ImportConfigModal } from '../components/ImportConfigModal';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Bozza', color: 'bg-gray-100 text-gray-700' },
  valid: { label: 'Validato', color: 'bg-blue-100 text-blue-700' },
  exported: { label: 'Esportato', color: 'bg-green-100 text-green-700' },
};

export function ProjectsListPage() {
  const canEdit = useCanEdit();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [showImportModal, setShowImportModal] = useState(false);
  
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-filter'],
    queryFn: () => clientsApi.list({ active: true, limit: 100 }),
  });
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', { q: search, status: statusFilter, clientId: clientFilter }],
    queryFn: () => projectsApi.list({
      q: search || undefined,
      status: statusFilter || undefined,
      clientId: clientFilter || undefined,
      limit: 100,
    }),
  });
  
  const projects = data?.data.data.projects || [];
  const clients = clientsData?.data.data.clients || [];
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Progetti</h1>
          <p className="text-gray-600">Configurazioni dei siti</p>
        </div>
        {canEdit && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-secondary"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importa
            </button>
            <Link to="/projects/new" className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo progetto
            </Link>
          </div>
        )}
      </div>
      
      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca progetti..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          
          <div className="flex gap-4">
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="input min-w-[180px]"
            >
              <option value="">Tutti i clienti</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input min-w-[140px]"
            >
              <option value="">Tutti gli stati</option>
              <option value="draft">Bozza</option>
              <option value="valid">Validato</option>
              <option value="exported">Esportato</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* List */}
      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">Errore nel caricamento</div>
        ) : projects.length === 0 ? (
          <div className="p-8 text-center">
            <FolderKanban className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">Nessun progetto trovato</p>
            {canEdit && (
              <div className="flex items-center justify-center space-x-3">
                <button
                  onClick={() => setShowImportModal(true)}
                  className="btn-secondary"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importa esistente
                </button>
                <Link to="/projects/new" className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Crea nuovo
                </Link>
              </div>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {projects.map((project) => {
              const client = typeof project.clientId === 'object' ? project.clientId : null;
              const status = STATUS_LABELS[project.status] || STATUS_LABELS.draft;
              
              return (
                <li key={project.id}>
                  <Link
                    to={`/projects/${project.id}`}
                    className="flex items-center px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                      <FolderKanban className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-gray-900 truncate">
                          {project.name}
                        </p>
                        <span className={clsx(
                          'text-xs px-2 py-0.5 rounded-full',
                          status.color
                        )}>
                          {status.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {client?.name || 'Cliente'}
                        <span className="mx-2">•</span>
                        Aggiornato {new Date(project.updatedAt).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Import Modal */}
      <ImportConfigModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
      />
    </div>
  );
}
